import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Printer, FileDown, MessageCircle, TrendingUp, Wallet, Percent, ReceiptText } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import api from "@/lib/api";
import { rupiah, rupiahShort } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";

function Stat({ icon: Icon, label, value, tone }) {
  return (
    <Card className="rounded-2xl p-5">
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${tone}`}><Icon className="h-5 w-5" /></span>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
          <p className="font-mono text-xl font-bold">{value}</p>
        </div>
      </div>
    </Card>
  );
}

const PDF_NAME = "Laporan-KalkuBisnis.pdf";
const SHARE_TEXT = "Laporan & Analisis KalkuBisnis — ringkasan 30 hari terakhir.";

export default function Laporan() {
  const { data } = useQuery({ queryKey: ["report-full"], queryFn: async () => (await api.get("/reports/summary")).data });
  const printRef = useRef(null);
  const pdfRef = useRef(null);
  const [busy, setBusy] = useState(null);
  const exporting = busy !== null;

  const buildPdf = async () => {
    const el = pdfRef.current;
    const canvas = await html2canvas(el, {
      scale: 1.5,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
    });
    const imgData = canvas.toDataURL("image/jpeg", 0.82);
    const pdf = new jsPDF("p", "mm", "a4");
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const headerH = 16;
    const usableW = pageW - margin * 2;
    const imgH = (canvas.height * usableW) / canvas.width;

    // Branding header (first page only) — no browser header/footer or URL.
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.setTextColor(16, 143, 91);
    pdf.text("KalkuBisnis", margin, margin + 6);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(90, 90, 90);
    pdf.text("Laporan & Analisis — Ringkasan 30 hari terakhir", margin, margin + 11);

    let position = margin + headerH;
    pdf.addImage(imgData, "JPEG", margin, position, usableW, imgH, "report", "FAST");
    let remaining = imgH - (pageH - position - margin);
    while (remaining > 0) {
      pdf.addPage();
      position = margin - (imgH - remaining);
      pdf.addImage(imgData, "JPEG", margin, position, usableW, imgH, "report", "FAST");
      remaining -= (pageH - margin * 2);
    }
    return pdf;
  };

  const exportPdf = async () => {
    if (!pdfRef.current || exporting) return;
    setBusy("pdf");
    try {
      (await buildPdf()).save(PDF_NAME);
      toast.success("PDF berhasil dibuat.");
    } catch (err) {
      toast.error("Gagal membuat PDF. Coba lagi.");
    } finally {
      setBusy(null);
    }
  };

  const shareFile = (file) => navigator.share({ files: [file], title: "Laporan KalkuBisnis", text: SHARE_TEXT });

  const shareWhatsApp = async () => {
    if (!pdfRef.current || exporting) return;
    setBusy("wa");
    try {
      const pdf = await buildPdf();
      const file = new File([pdf.output("blob")], PDF_NAME, { type: "application/pdf" });
      if (navigator.canShare?.({ files: [file] })) {
        try {
          await shareFile(file);
        } catch (err) {
          if (err.name === "NotAllowedError") {
            toast.success("PDF siap dibagikan.", { action: { label: "Bagikan", onClick: () => shareFile(file).catch(() => {}) }, duration: 15000 });
          }
        }
      } else {
        pdf.save(PDF_NAME);
        window.open(`https://wa.me/?text=${encodeURIComponent(SHARE_TEXT)}`, "_blank", "noopener");
        toast.success("PDF diunduh. Lampirkan file tersebut di WhatsApp.");
      }
    } catch (err) {
      toast.error("Gagal menyiapkan PDF. Coba lagi.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 no-print">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Laporan & Analisis</h1>
          <p className="text-sm text-muted-foreground">Ringkasan penjualan 30 hari terakhir.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" className="rounded-xl min-h-[44px]" onClick={() => window.print()} data-testid="report-print-button">
            <Printer className="mr-2 h-4 w-4" /> Cetak Laporan
          </Button>
          <Button className="rounded-xl min-h-[44px]" onClick={exportPdf} disabled={exporting} data-testid="report-export-pdf-button">
            <FileDown className="mr-2 h-4 w-4" /> {busy === "pdf" ? "Menyiapkan..." : "Export PDF"}
          </Button>
          <Button className="rounded-xl min-h-[44px] bg-[#128C7E] text-white hover:bg-[#0f7569]" onClick={shareWhatsApp} disabled={exporting} data-testid="report-share-whatsapp-button">
            <MessageCircle className="mr-2 h-4 w-4" /> {busy === "wa" ? "Menyiapkan..." : "Kirim ke WhatsApp"}
          </Button>
        </div>
      </div>

      <div ref={printRef} className="print-area space-y-6">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <Stat icon={Wallet} label="Total Omset" value={rupiah(data?.total_revenue)} tone="bg-primary/10 text-primary" />
          <Stat icon={TrendingUp} label="Total Untung" value={rupiah(data?.total_profit)} tone="bg-teal-500/10 text-teal-600 dark:text-teal-400" />
          <Stat icon={Percent} label="Margin (% dari Harga Jual)" value={`${data?.avg_margin || 0}%`} tone="bg-amber-500/10 text-amber-600 dark:text-amber-400" />
          <Stat icon={Percent} label="Markup (% dari HPP)" value={`${data?.avg_markup || 0}%`} tone="bg-orange-500/10 text-orange-600 dark:text-orange-400" />
          <Stat icon={ReceiptText} label="Total Transaksi" value={data?.transactions || 0} tone="bg-blue-500/10 text-blue-600 dark:text-blue-400" />
        </div>

        <Card className="rounded-2xl p-5">
          <h3 className="mb-4 font-heading text-lg font-semibold">Tren Omset & Untung Harian</h3>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.daily || []} margin={{ left: 0, right: 12, top: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" interval={4} />
                <YAxis tickFormatter={rupiahShort} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={60} />
                <Tooltip formatter={(v, n) => [rupiah(v), n === "revenue" ? "Omset" : "Untung"]}
                  contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => (v === "revenue" ? "Omset" : "Untung")} />
                <Line type="monotone" dataKey="revenue" stroke="hsl(var(--chart-1))" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="profit" stroke="hsl(var(--chart-2))" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card className="rounded-2xl p-5">
            <h3 className="mb-4 font-heading text-lg font-semibold">Produk Terlaris</h3>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data?.top_products || []} layout="vertical" margin={{ left: 8, right: 12 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.4} horizontal={false} />
                  <XAxis type="number" tickFormatter={rupiahShort} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={110} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip formatter={(v) => rupiah(v)} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }} />
                  <Bar dataKey="revenue" fill="hsl(var(--chart-1))" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="overflow-hidden rounded-2xl">
            <div className="p-5 pb-2"><h3 className="font-heading text-lg font-semibold">Rincian per Produk</h3></div>
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/60">
                  <TableHead>Produk</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Omset</TableHead>
                  <TableHead className="text-right">Untung</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.top_products || []).map((p) => (
                  <TableRow key={p.name}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-right font-mono">{p.quantity}</TableCell>
                    <TableCell className="text-right font-mono">{rupiah(p.revenue)}</TableCell>
                    <TableCell className="text-right font-mono text-primary">{rupiah(p.profit)}</TableCell>
                  </TableRow>
                ))}
                {(!data?.top_products || data.top_products.length === 0) && (
                  <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">Belum ada data penjualan.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      </div>

      {/* Hidden, fixed-width layout captured only for the clean PDF export. */}
      <PdfReport data={data} innerRef={pdfRef} />
    </div>
  );
}

function PdfStat({ label, value, color }) {
  return (
    <div style={{ flex: "1 1 138px", minWidth: 138, border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", boxSizing: "border-box" }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4, color: "#6b7280" }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: color || "#111827", whiteSpace: "nowrap", marginTop: 4 }}>{value}</div>
    </div>
  );
}

function PdfReport({ data, innerRef }) {
  const daily = data?.daily || [];
  const products = data?.top_products || [];
  const labelInterval = daily.length > 10 ? Math.ceil(daily.length / 8) - 1 : 0;
  const cell = { padding: "8px 10px", fontSize: 12, borderBottom: "1px solid #e5e7eb" };

  return (
    <div
      ref={innerRef}
      aria-hidden="true"
      data-testid="pdf-report"
      style={{
        position: "absolute", left: -10000, top: 0, width: 800, background: "#ffffff",
        color: "#111827", fontFamily: "Arial, Helvetica, sans-serif", padding: 20, boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
        <PdfStat label="Total Omset" value={rupiah(data?.total_revenue)} />
        <PdfStat label="Total Untung" value={rupiah(data?.total_profit)} color="#0d9488" />
        <PdfStat label="Margin (% Harga Jual)" value={`${data?.avg_margin || 0}%`} />
        <PdfStat label="Markup (% dari HPP)" value={`${data?.avg_markup || 0}%`} />
        <PdfStat label="Total Transaksi" value={String(data?.transactions || 0)} />
      </div>

      <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Tren Omset & Untung Harian</div>
        <div style={{ display: "flex", gap: 16, marginBottom: 6, fontSize: 11, color: "#374151" }}>
          <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#10b981", marginRight: 5, verticalAlign: "middle" }} />Omset</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#0d9488", marginRight: 5, verticalAlign: "middle" }} />Untung</span>
        </div>
        <LineChart width={760} height={260} data={daily} margin={{ top: 8, right: 36, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#6b7280" }} stroke="#9ca3af" interval={labelInterval} />
          <YAxis tickFormatter={rupiahShort} tick={{ fontSize: 10, fill: "#6b7280" }} stroke="#9ca3af" width={64} />
          <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={false} isAnimationActive={false} />
          <Line type="monotone" dataKey="profit" stroke="#0d9488" strokeWidth={2} dot={false} isAnimationActive={false} />
        </LineChart>
      </div>

      <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Produk Terlaris</div>
        <BarChart
          width={760}
          height={Math.max(160, products.length * 42 + 40)}
          data={products}
          layout="vertical"
          margin={{ top: 4, right: 36, left: 8, bottom: 4 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
          <XAxis type="number" tickFormatter={rupiahShort} tick={{ fontSize: 10, fill: "#6b7280" }} stroke="#9ca3af" />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: "#6b7280" }} stroke="#9ca3af" width={130} />
          <Bar dataKey="revenue" fill="#10b981" radius={[0, 4, 4, 0]} isAnimationActive={false} />
        </BarChart>
      </div>

      <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ fontSize: 14, fontWeight: 600, padding: "12px 12px 4px" }}>Rincian per Produk</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f1f5f9" }}>
              <th style={{ ...cell, textAlign: "left", fontWeight: 600 }}>Produk</th>
              <th style={{ ...cell, textAlign: "right", fontWeight: 600, width: 70 }}>Qty</th>
              <th style={{ ...cell, textAlign: "right", fontWeight: 600, width: 150, whiteSpace: "nowrap" }}>Omset</th>
              <th style={{ ...cell, textAlign: "right", fontWeight: 600, width: 150, whiteSpace: "nowrap" }}>Untung</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.name}>
                <td style={{ ...cell, textAlign: "left", fontWeight: 600 }}>{p.name}</td>
                <td style={{ ...cell, textAlign: "right" }}>{p.quantity}</td>
                <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>{rupiah(p.revenue)}</td>
                <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap", color: "#0d9488" }}>{rupiah(p.profit)}</td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr><td colSpan={4} style={{ ...cell, textAlign: "center", color: "#6b7280", padding: 24 }}>Belum ada data penjualan.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
